'use client'
import { useEffect } from 'react'

/**
 * Production tripwire for hydration failures.
 *
 * React in production says NOTHING when the browser's build of a page
 * disagrees with the server's: it discards the server tree, rebuilds, and the
 * page looks fine. The cost is invisible — a full re-render on every load, and
 * every inline <script> React can't dedupe emitted twice, JSON-LD included.
 * That is how one unpinned date put two aggregateRating nodes on every page of
 * adventureelevated.com for a month before Search Console noticed (#1282).
 *
 * This reports the next one the day it ships. React surfaces recoverable
 * hydration errors through `reportError`, i.e. a window `error` event — but
 * those fire DURING hydration, before any effect can subscribe. So the layout's
 * inline HYDRATION_STASH script (which runs before the bundle) collects them
 * into `window.__sfHydErr`, and this effect drains the stash and keeps
 * listening for boundaries that hydrate late. One beacon per page load; the
 * route rate-limits per IP. Payload is the path and the error codes — nothing
 * about the visitor.
 */

declare global {
  interface Window {
    __sfHydErr?: string[]
  }
}

export const HYDRATION_ERROR_RE = /Minified React error #4(18|19|2[0-5])|hydrat/i

export function HydrationErrorBeacon() {
  useEffect(() => {
    let sent = false
    const flush = () => {
      const errors = window.__sfHydErr
      if (sent || !errors || errors.length === 0) return
      sent = true
      const body = JSON.stringify({ path: location.pathname, errors: errors.slice(0, 5) })
      try {
        if (!navigator.sendBeacon?.('/api/hydration-error', new Blob([body], { type: 'application/json' }))) {
          void fetch('/api/hydration-error', { method: 'POST', body, headers: { 'content-type': 'application/json' }, keepalive: true })
        }
      } catch {
        /* reporting must never itself break a page */
      }
    }
    // Late boundaries (selective hydration) can still fail after mount.
    const onError = (e: ErrorEvent) => {
      const msg = e.error?.message ?? e.message ?? ''
      if (!HYDRATION_ERROR_RE.test(msg)) return
      ;(window.__sfHydErr ??= []).push(String(msg).slice(0, 200))
      flush()
    }
    window.addEventListener('error', onError)
    flush()
    return () => window.removeEventListener('error', onError)
  }, [])
  return null
}
